import './index.scss';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../../shared/Datepicker';
import React, { useEffect, useState } from 'react';
import { getSingleOption } from '../../../../utility/Utils';
import { Col, FormFeedback, Label, Row } from 'reactstrap';
import { Controller, UseFormReturn } from 'react-hook-form';
import { GymResource } from '../../../../store/gym-management/GymTypes';
import getCurrentHourOption from '../../../../utility/select-options/get-current-hour-option';

interface EventDateInformationProps {
  originalArray: number[];
  form: UseFormReturn<any, object>;
  gymRoom: GymResource;
}

const BookGymDateInformation = (props: EventDateInformationProps) => {
  const { minimumBookingTime, maximumBookingTime, availableTo, availableFrom } =
    props.gymRoom;

  const {
    getValues,
    formState: { errors },
  } = props.form;

  const { t } = useTranslation();
  const [startingTimeOptions, setStartingTimeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [endingTimeOptions, setEndingTimeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const TIME_OPTIONS_STEP = 0.25;

  const getStartingTimeOptions = () => {
    const currentDate = new Date().toDateString();
    const isToday =
      new Date(props.form.watch('date')).toDateString() === currentDate;

    const currentHourOption = getCurrentHourOption();

    const startIndex =
      isToday && currentHourOption > availableFrom
        ? currentHourOption + 0.25
        : availableFrom;

    const options = [];
    for (
      let i = startIndex;
      i <= +availableTo - minimumBookingTime;
      i += TIME_OPTIONS_STEP
    ) {
      if (!props.originalArray[i * 4] || props.originalArray[i * 4] === 2) {
        const option = getSingleOption(i);

        if (
          isMinBookingCorrect(
            props.originalArray,
            props.gymRoom.minimumBookingTime,
            +option.value
          )
        ) {
          options.push(option);
        }
      }
    }

    if (!options.length) {
      return [{ value: '', label: t('NO_AVAILABLE_OPTIONS_FOR_THIS_DATE') }];
    }

    return options;
  };

  const isMinBookingCorrect = (
    originalArray: number[],
    minBookingTime: number,
    index: number
  ): boolean => {
    const minBookingTimeSteps = minBookingTime * 4;

    const startIndex = index * 4;
    const endIndex = Math.min(
      startIndex + minBookingTimeSteps,
      originalArray.length
    );

    for (let i = startIndex; i < endIndex; i++) {
      if (originalArray[i] && originalArray[i] !== 2) {
        return false;
      }
    }
    return true;
  };

  const getEndingTimeOptions = () => {
    const userStartingTime = parseFloat(getValues().start);
    const options = [];

    const firstOption = userStartingTime + minimumBookingTime;
    const lastOption = userStartingTime + maximumBookingTime;

    for (
      let i = firstOption;
      i <= lastOption && i <= availableTo;
      i += TIME_OPTIONS_STEP
    ) {
      const index = i / TIME_OPTIONS_STEP;
      if (props.originalArray[index - 1] === 1) {
        i = availableTo;
      } else {
        const option = getSingleOption(i);

        options.push(option);
      }
    }

    return options;
  };

  const maxNumberOfPeople = props.gymRoom.capacity;

  useEffect(() => {
    setStartingTimeOptions(getStartingTimeOptions());
    const options = getEndingTimeOptions();

    setEndingTimeOptions(options);
    if (options.length === 1) {
      props.form.setValue('end', +options[0].value);
    }
  }, [
    props.originalArray,
    props.form.watch('date'),
    props.form.watch('start'),
  ]);

  return (
    <div className="book-gym-form-date-time">
      <Row>
        <Col md="12" className="form-input">
          <Label for="date">
            <span>* </span>
            {t('DATE_LABEL')}
          </Label>
          <Controller
            name="date"
            control={props.form.control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            render={({ field }) => (
              <DatePicker
                id="date"
                className="text-input"
                minDate="today"
                placeholder={t('DATE_LABEL')}
                {...field}
                allowedDays={props.gymRoom?.availableDays}
                no_clear
                onChange={(e) => {
                  props.form.setValue('start', '');
                  props.form.setValue('end', '');
                  field.onChange(e);
                }}
              />
            )}
          />
          {errors.date ? (
            <FormFeedback className="form-input-error">
              {errors.date}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="start">
            <span>* </span>
            {t('START')}
          </Label>
          <Controller
            control={props.form.control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            name="start"
            render={({ field: { onChange, value } }) => (
              <Select
                value={startingTimeOptions.filter((c) => +value === +c.value)}
                id="start"
                onChange={(val) => {
                  onChange(val?.value);
                }}
                options={startingTimeOptions}
              />
            )}
          />
          {errors.start ? (
            <FormFeedback className="form-input-error">
              {t('FORM_ERRORS_FIELD_IS_REQUIRED')}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="end">
            <span>* </span>
            {t('END')}
          </Label>
          <Controller
            control={props.form.control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            name="end"
            render={({ field: { onChange, value } }) => (
              <Select
                isDisabled={!props.form.watch('start')}
                value={endingTimeOptions.filter((c) => +value === +c.value)}
                id="end"
                onChange={(val) => {
                  onChange(val?.value);
                }}
                options={endingTimeOptions}
              />
            )}
          />
          {errors.end ? (
            <FormFeedback className="form-input-error">
              {t('FORM_ERRORS_FIELD_IS_REQUIRED')}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
    </div>
  );
};

export default BookGymDateInformation;
